import React from 'react';

/**
 * Rede de seguranca da aplicacao inteira.
 *
 * POR QUE EXISTE: nao havia NENHUM error boundary no projeto. Qualquer excecao durante o render
 * desmontava a arvore toda e deixava a tela BRANCA, sem mensagem e sem saida — aconteceu em
 * producao no meio do checkout da assinatura, que e o pior lugar possivel para isso.
 *
 * O caso mais comum e o ChunkLoadError. O app e quase todo `lazy()`, e cada deploy publica
 * chunks com hash novo e remove os antigos. Uma aba que ficou aberta durante um deploy carrega
 * um bundle que conhece nomes de arquivo que nao existem mais: ao navegar para uma rota ainda
 * nao baixada, o import dinamico toma 404. Isso atinge QUALQUER usuario com a aba aberta, a
 * cada deploy — nao e um caso raro.
 *
 * Para esse caso a recuperacao e trivial e automatica: recarregar busca o index.html novo, com
 * os nomes de chunk certos. Recarrega UMA vez por sessao (marca em sessionStorage); se falhar de
 * novo, o problema nao e chunk velho e a pessoa ve a tela de erro em vez de um loop de reload.
 */

const CHAVE_RELOAD = 'maestra:chunk-reload';

// A mensagem varia por navegador; o nome do erro nem sempre vem. Casar pelos dois lados.
export function ehErroDeChunk(error: unknown): boolean {
  const e = error as { name?: string; message?: string } | null;
  const nome = e?.name || '';
  const msg = e?.message || '';
  return (
    nome === 'ChunkLoadError' ||
    /Loading chunk .* failed/i.test(msg) ||
    /Loading CSS chunk .* failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg)
  );
}

// A marca vale por um minuto. Ela era apagada a cada carregamento do pacote (no index.tsx), e o
// pacote sempre carrega antes do pedaço que falha: o "uma vez" virava "para sempre". Foi o laço
// do admin no www em 03/11/2026, com um pedaço que voltava como HTML do cache.
const JANELA_DO_RELOAD_MS = 60_000;

/** Recarrega uma vez por minuto. Devolve false quando ja tentou (para nao entrar em loop). */
export function tentarRecarregarUmaVez(): boolean {
  try {
    const ultima = Number(sessionStorage.getItem(CHAVE_RELOAD));
    if (ultima && Date.now() - ultima < JANELA_DO_RELOAD_MS) return false;
    sessionStorage.setItem(CHAVE_RELOAD, String(Date.now()));
  } catch {
    // Navegacao anonima pode bloquear sessionStorage. Sem o guarda o risco de loop e real,
    // entao nao recarrega: melhor mostrar a tela de erro do que piscar para sempre.
    return false;
  }
  window.location.reload();
  return true;
}

/** Some com a marca depois de um carregamento saudavel, liberando a proxima tentativa. */
export function limparMarcaDeReload(): void {
  try {
    sessionStorage.removeItem(CHAVE_RELOAD);
  } catch {
    /* sem sessionStorage, nada a limpar */
  }
}

interface Props { children: React.ReactNode }
interface State { erro: Error | null; recarregando: boolean; pilha: string | null }

/**
 * EM DESENVOLVIMENTO O CARTAO CONTA O QUE ACONTECEU.
 *
 * O texto para o usuario final tem de ser curto e sem jargao, e e o certo em producao. Mas quem
 * esta com o app aberto no localhost ve exatamente o mesmo cartao mudo, e a unica pista fica no
 * console — que e facil nao ter aberto. Cada erro de render vira uma ida e volta so para
 * descobrir a mensagem que o proprio boundary ja tem na mao.
 *
 * `NODE_ENV` e trocado pelo bundler em tempo de build, entao este bloco inteiro sai do pacote de
 * producao: nao ha risco de vazar pilha para cliente nenhum.
 */
const EM_DESENVOLVIMENTO = process.env.NODE_ENV === 'development';

class AppErrorBoundary extends React.Component<Props, State> {
  state: State = { erro: null, recarregando: false, pilha: null };

  static getDerivedStateFromError(erro: Error): State {
    return { erro, recarregando: false, pilha: null };
  }

  componentDidCatch(erro: Error, info: React.ErrorInfo) {
    if (ehErroDeChunk(erro)) {
      // Deploy novo com aba velha: recarregar resolve sozinho.
      if (tentarRecarregarUmaVez()) {
        this.setState({ recarregando: true });
        return;
      }
    }
    console.error('[app] erro nao tratado:', erro, info.componentStack);
    if (EM_DESENVOLVIMENTO) this.setState({ pilha: info.componentStack || null });
  }

  render() {
    const { erro, recarregando, pilha } = this.state;
    if (!erro) return this.props.children;

    // Recarregando: nao mostra erro, senao a pessoa ve um susto por um instante e some.
    if (recarregando) return <div style={estilos.tela} />;

    const chunk = ehErroDeChunk(erro);
    return (
      <div style={estilos.tela}>
        <div style={estilos.cartao}>
          <h1 style={estilos.titulo}>
            {chunk ? 'Atualize a pagina para continuar' : 'Algo deu errado por aqui'}
          </h1>
          <p style={estilos.texto}>
            {chunk
              ? 'Saiu uma versao nova do Maestra enquanto esta aba estava aberta. Recarregar resolve.'
              : 'Nao conseguimos carregar esta tela. Recarregar costuma resolver; se insistir, fale com o suporte.'}
          </p>
          <button
            style={estilos.botao}
            onClick={() => { limparMarcaDeReload(); window.location.reload(); }}
          >
            Recarregar
          </button>

          {EM_DESENVOLVIMENTO && !chunk && (
            <div style={estilos.detalhe}>
              <div style={estilos.detalheTitulo}>{erro.name}: {erro.message}</div>
              {pilha && <pre style={estilos.detalhePilha}>{pilha.trim()}</pre>}
            </div>
          )}
        </div>
      </div>
    );
  }
}

const estilos: Record<string, React.CSSProperties> = {
  tela: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--cds-layer-01)',
    padding: 20,
    fontFamily: 'var(--font-body)',
  },
  cartao: {
    maxWidth: 420,
    width: '100%',
    background: 'var(--cds-layer-01)',
    border: '1px solid var(--cds-border-subtle-01)',
    borderRadius: 14,
    padding: '28px 26px',
    textAlign: 'center',
  },
  titulo: { fontSize: 19, fontWeight: 800, color: 'var(--cds-text-primary)', margin: '0 0 10px' },
  texto: { fontSize: 14, lineHeight: 1.55, color: 'var(--cds-text-primary)', margin: '0 0 22px' },
  detalhe: {
    marginTop: 22,
    paddingTop: 18,
    borderTop: '1px solid var(--cds-border-subtle-01)',
    textAlign: 'left',
  },
  detalheTitulo: {
    fontSize: 13,
    fontWeight: 700,
    color: 'var(--cds-text-error)',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    wordBreak: 'break-word',
    marginBottom: 10,
  },
  detalhePilha: {
    fontSize: 11,
    lineHeight: 1.5,
    color: 'var(--cds-text-helper)',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    margin: 0,
    maxHeight: 220,
    overflow: 'auto',
    whiteSpace: 'pre-wrap',
  },
  botao: {
    background: 'var(--cds-interactive)',
    color: 'var(--cds-text-on-color)',
    border: 'none',
    borderRadius: 9999,
    padding: '12px 28px',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
  },
};

export default AppErrorBoundary;
